import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  REFRESH_INTEGRATION_AUTH_DATA_TIMEOUT_MS,
  type ModelAddIntegrationResponse,
  type ModelSocialIntegration,
  useGetIntegrationAuthStateQuery,
  useLazyRefreshIntegrationAuthDataQuery,
  useRestartIntegrationMutation,
} from '@app/api/SocialIntegrations';
import { ERROR } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { addWsEventListener } from '@app/utils/createWSConnection';

import {
  isEventForToken,
  parseWhatsAppSenderQrEvent,
} from '@pages/Integrations/IntegrationsList/lib/parseWhatsAppSenderQrEvent';
import {
  FINISHING_FAILED_MESSAGE,
  PREPARING_FAILED_MESSAGE,
  resolveWhatsAppAuthPhase,
  WhatsAppAuthPhase,
} from '@pages/Integrations/IntegrationsList/lib/whatsAppAuthPhase';

/** Состояние интеграции опрашивается дёшево: запрос читает два поля из БД CRM. */
const STATUS_POLLING_MS = 2500;
const STATUS_FINAL_CHECK_TIMEOUT_MS = 5_000;

/** QR обновляется реже: за ним стоит обращение к шлюзу. */
const QR_POLLING_MS = 15_000;

/**
 * Сколько ждём, пока перезапущенная интеграция встанет в «Подключается».
 * На проде подъём сокета занимает 13–15 секунд, здесь запас втрое.
 */
const PREPARING_TIMEOUT_MS = 45_000;

/**
 * Сколько ждём подтверждения после того, как код отсканирован. Больше
 * наблюдавшихся на проде 22–26 секунд и с запасом на обогащение данных
 * аккаунта на стороне CRM (issue/11029).
 */
const FINISHING_TIMEOUT_MS = 90_000;

/**
 * Сколько подряд неудачных запросов кода терпим, прежде чем сказать об этом
 * человеку. Одну осечку глушим намеренно: код опрашивается раз в 15 секунд,
 * и разрывать окно из-за единственного сетевого сбоя незачем.
 */
const QR_FAILURES_BEFORE_ERROR = 2;

const QR_FAILED_MESSAGE =
  'Не удалось получить QR-код от шлюза. Попробуйте ещё раз';

const RESTART_FAILED_MESSAGE =
  'Не удалось перезапустить интеграцию. Попробуйте ещё раз';

export interface WhatsAppAuthSession {
  /**
   * Номер захода. Идентификатора интеграции для сверки мало: повтор открывает
   * НОВУЮ сессию того же аккаунта, и ответ прежнего запроса проходил проверку
   * по идентификатору и менял уже новую сессию (issue/11029).
   */
  generation: number;
  integrationId: number;
  /** Токен инстанса; нужен только для сверки события, опрос без него работает. */
  expectedToken: string | null;
  qr: string | null;
  source: 'new' | 'existing';
  phase: WhatsAppAuthPhase;
  errorMessage?: string;
  /**
   * Момент старта сессии. Ответы состояния, завершившиеся раньше него, не
   * учитываются: RTK Query отдаёт последний УСПЕШНЫЙ ответ независимо от
   * аргумента и держит его в кэше минуту, поэтому без этой отсечки окно
   * нового аккаунта показывало успех предыдущего (issue/11029).
   */
  startedAt: number;
}

interface UseWhatsAppAuthorizationParams {
  /** Вызывается один раз при подтверждённом успехе: обновить реестр. */
  onSuccess: () => void | Promise<void>;
}

interface AbortableRequest<T> {
  unwrap(): Promise<T>;
  abort?(): void;
}

/**
 * Ограничивает ожидание ответа и отменяет сам HTTP-запрос. Один Promise.race
 * освобождал UI, но Axios продолжал занимать соединение в фоне.
 */
const withRequestTimeout = async <T>(
  request: AbortableRequest<T>,
  limitMs: number
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      request.unwrap(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          request.abort?.();
          reject(new Error('Ответ на запрос кода не пришёл вовремя'));
        }, limitMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const PHASE_TIMEOUTS: Partial<
  Record<WhatsAppAuthPhase, { limitMs: number; message: string }>
> = {
  [WhatsAppAuthPhase.Preparing]: {
    limitMs: PREPARING_TIMEOUT_MS,
    message: PREPARING_FAILED_MESSAGE,
  },
  [WhatsAppAuthPhase.Finishing]: {
    limitMs: FINISHING_TIMEOUT_MS,
    message: FINISHING_FAILED_MESSAGE,
  },
};

/**
 * Единая сессия авторизации WhatsApp — и для новой интеграции, и для повторного
 * входа в существующую (issue/11029).
 *
 * Источник истины — состояние интеграции в CRM, которое опрашивается всё время,
 * пока открыто окно. Событие по вебсокету только ускоряет реакцию: раньше оно
 * было единственным условием успеха, и его потеря оставляла окно с прежним QR
 * навсегда.
 */
export const useWhatsAppAuthorization = ({
  onSuccess,
}: UseWhatsAppAuthorizationParams) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const [session, setSession] = useState<WhatsAppAuthSession | null>(null);
  const [refreshIntegrationAuthData] = useLazyRefreshIntegrationAuthDataQuery();
  const [restartIntegration] = useRestartIntegrationMutation();

  /** Момент входа в фазу — из него считается её предел ожидания. */
  const phaseStartedAt = useRef<number | null>(null);
  /** Счётчик заходов: каждый новый получает свой номер. */
  const generation = useRef(0);
  /** Номер захода, чей запрос кода сейчас в полёте; null — свободно. */
  const qrRequestGeneration = useRef<number | null>(null);
  /** RTK action умеет отменить Axios через signal, в отличие от Promise.race. */
  const qrRequest = useRef<ReturnType<
    typeof refreshIntegrationAuthData
  > | null>(null);
  /** Успех объявляется один раз, даже если событие и опрос пришли вместе. */
  const successHandled = useRef(false);
  /** Неудачные запросы кода подряд. */
  const qrFailures = useRef(0);

  const isOpen = session !== null;
  const sessionIntegrationId = session?.integrationId ?? null;
  const sessionPhase = session?.phase ?? null;
  const isFinished =
    sessionPhase === WhatsAppAuthPhase.Success ||
    sessionPhase === WhatsAppAuthPhase.Error;

  const {
    currentData: authState,
    fulfilledTimeStamp,
    refetch: refetchAuthState,
  } = useGetIntegrationAuthStateQuery(
    session && !isFinished
      ? { ActiveBranchId, integrationId: session.integrationId }
      : skipToken,
    {
      pollingInterval: STATUS_POLLING_MS,
      // Кэш прошлого открытия того же аккаунта живёт минуту: без этого
      // повторный вход стартовал бы с прежним статусом.
      refetchOnMountOrArgChange: true,
    }
  );

  /**
   * Статус засчитываем только из ответа, завершившегося уже в текущей сессии.
   * `currentData` отсекает данные другого аккаунта, метка времени — данные
   * прошлого открытия того же аккаунта.
   */
  const freshStatus =
    session &&
    fulfilledTimeStamp !== undefined &&
    fulfilledTimeStamp >= session.startedAt
      ? authState?.Status
      : undefined;

  const abortQrRequest = useCallback(() => {
    qrRequest.current?.abort?.();
    qrRequest.current = null;
    qrRequestGeneration.current = null;
  }, []);

  const close = useCallback(() => {
    generation.current += 1;
    abortQrRequest();
    setSession(null);
    phaseStartedAt.current = null;
    successHandled.current = false;
    qrFailures.current = 0;
  }, [abortQrRequest]);

  const startSession = useCallback(
    (next: Omit<WhatsAppAuthSession, 'startedAt' | 'generation'>) => {
      // Новая сессия всегда начинается с чистого листа: иначе между аккаунтами
      // переносятся QR, счётчики и отсечка свежести ответов.
      abortQrRequest();
      const startedAt = Date.now();
      generation.current += 1;
      phaseStartedAt.current = startedAt;
      successHandled.current = false;
      qrFailures.current = 0;
      setSession({ ...next, startedAt, generation: generation.current });
    },
    [abortQrRequest]
  );

  useEffect(
    () => () => {
      generation.current += 1;
      abortQrRequest();
    },
    [abortQrRequest]
  );

  /** Переводит текущую сессию в ошибку, если это всё ещё она. */
  const failSession = useCallback(
    (sessionGeneration: number, message: string) => {
      setSession((current) =>
        current &&
        current.generation === sessionGeneration &&
        current.phase !== WhatsAppAuthPhase.Success
          ? {
              ...current,
              qr: null,
              phase: WhatsAppAuthPhase.Error,
              errorMessage: message,
            }
          : current
      );
    },
    []
  );

  /**
   * Перезапуск интеграции: единственный способ вернуть её в «Подключается»,
   * из которого CRM отдаёт код.
   */
  const restartAndPrepare = useCallback(
    async (integrationId: number, sessionGeneration: number) => {
      try {
        await restartIntegration({
          ActiveBranchId,
          integrationId,
        }).unwrap();
      } catch {
        failSession(sessionGeneration, RESTART_FAILED_MESSAGE);
      }
    },
    [ActiveBranchId, failSession, restartIntegration]
  );

  const openNew = useCallback(
    (created: ModelAddIntegrationResponse) => {
      // Новая интеграция создаётся сразу в «Подключается» и приносит код в
      // ответе — ждать нечего.
      startSession({
        integrationId: created.IntegrationId,
        expectedToken: created.InstanceToken ?? null,
        qr: created.QR ?? null,
        source: 'new',
        phase: WhatsAppAuthPhase.Qr,
      });
    },
    [startSession]
  );

  const openExisting = useCallback(
    async (integration: ModelSocialIntegration) => {
      // Код не запрашиваем сразу: CRM отдаёт данные авторизации только для
      // статуса «Подключается», а на любой другой отвечает пустотой, которую
      // окно принимало за конец входа (issue/11029).
      startSession({
        integrationId: integration.Id,
        expectedToken: integration.Token ?? null,
        qr: null,
        source: 'existing',
        phase: WhatsAppAuthPhase.Preparing,
      });

      // Перезапускаем только то, что лежит: у «Подключается» сокет уже
      // поднимается, и рестарт лишь отбросил бы человека назад. Фазу
      // подготовки всё равно проходим — код запрашиваем лишь после того, как
      // свежий ответ подтвердит «Подключается».
      if (integration.Status === ERROR) {
        await restartAndPrepare(integration.Id, generation.current);
      }
    },
    [restartAndPrepare, startSession]
  );

  /** Повтор после ошибки: заново перезапускаем интеграцию и ждём код. */
  const retry = useCallback(async () => {
    const current = session;
    if (!current) return;

    // Повтор — это новый заход, а не продолжение прежнего: ответы старых
    // запросов не должны его трогать.
    abortQrRequest();
    const startedAt = Date.now();
    generation.current += 1;
    const sessionGeneration = generation.current;
    phaseStartedAt.current = startedAt;
    successHandled.current = false;
    qrFailures.current = 0;

    setSession({
      ...current,
      qr: null,
      errorMessage: undefined,
      phase: WhatsAppAuthPhase.Preparing,
      startedAt,
      generation: sessionGeneration,
    });

    await restartAndPrepare(current.integrationId, sessionGeneration);
  }, [abortQrRequest, restartAndPrepare, session]);

  /** Запрашивает QR. Пустой ответ означает, что кода больше нет. */
  const refreshQr = useCallback(async () => {
    if (!session || session.phase !== WhatsAppAuthPhase.Qr) return;

    const { integrationId, generation: sessionGeneration } = session;

    // Один запрос за раз. Иначе на медленном шлюзе интервал успевает послать
    // второй, оба ждут один и тот же отказ, и порог «две неудачи подряд»
    // вырождается в «одна» (issue/11029).
    if (qrRequestGeneration.current !== null) return;
    qrRequestGeneration.current = sessionGeneration;
    const request = refreshIntegrationAuthData({
      ActiveBranchId,
      integrationId,
    });
    qrRequest.current = request;

    try {
      const result = await withRequestTimeout(
        request,
        REFRESH_INTEGRATION_AUTH_DATA_TIMEOUT_MS
      );

      // Ответ мог прийти после закрытия окна, смены аккаунта или повтора.
      if (generation.current !== sessionGeneration) return;

      qrFailures.current = 0;

      setSession((current) => {
        if (!current || current.generation !== sessionGeneration)
          return current;
        if (current.phase !== WhatsAppAuthPhase.Qr) return current;

        if (!result?.QR?.trim()) {
          // Кода больше нет — старый убираем и ждём подтверждения. Успехом это
          // не считаем: CRM возвращает как null всего ответа, так и объект с
          // QR=null/"" (в частности, когда шлюз уже видит авторизованный
          // аккаунт), а финальный статус приходит отдельным каналом.
          phaseStartedAt.current = Date.now();

          return {
            ...current,
            qr: null,
            phase: WhatsAppAuthPhase.Finishing,
          };
        }

        return { ...current, qr: result.QR };
      });
    } catch {
      // Молчать нельзя: раньше отказ шлюза оставлял человека наедине с
      // бесконечной загрузкой, а при создании интеграции — с недействительным
      // кодом на экране (issue/11029).
      if (generation.current !== sessionGeneration) return;

      qrFailures.current += 1;

      if (qrFailures.current >= QR_FAILURES_BEFORE_ERROR) {
        failSession(sessionGeneration, QR_FAILED_MESSAGE);
      }
    } finally {
      if (qrRequest.current === request) {
        qrRequest.current = null;
      }
      if (qrRequestGeneration.current === sessionGeneration) {
        qrRequestGeneration.current = null;
      }
    }
  }, [ActiveBranchId, failSession, refreshIntegrationAuthData, session]);

  /**
   * Интервал опроса кода должен пережить пересоздание refreshQr, но
   * перезапускаться только при смене аккаунта или фазы. Ссылка на свежую
   * функцию — в рефе; эффект от неё не зависит и обходится без подавления
   * правила зависимостей.
   */
  const refreshQrRef = useRef(refreshQr);

  useEffect(() => {
    refreshQrRef.current = refreshQr;
  }, [refreshQr]);

  useEffect(() => {
    if (
      sessionIntegrationId === null ||
      sessionPhase !== WhatsAppAuthPhase.Qr
    ) {
      return;
    }

    const run = () => {
      void refreshQrRef.current();
    };

    run();
    const interval = setInterval(run, QR_POLLING_MS);

    return () => clearInterval(interval);
  }, [sessionIntegrationId, sessionPhase]);

  /** Состояние интеграции решает судьбу сессии. */
  useEffect(() => {
    if (!session || isFinished) return;

    const elapsed = phaseStartedAt.current
      ? Date.now() - phaseStartedAt.current
      : 0;

    const { phase, errorMessage } = resolveWhatsAppAuthPhase({
      phase: session.phase,
      status: freshStatus,
      preparingElapsedMs: elapsed,
      preparingTimeoutMs: PREPARING_TIMEOUT_MS,
      finishingElapsedMs: elapsed,
      finishingTimeoutMs: FINISHING_TIMEOUT_MS,
    });

    if (phase === session.phase) return;

    phaseStartedAt.current = Date.now();
    qrFailures.current = 0;

    setSession((current) =>
      current ? { ...current, phase, errorMessage, qr: null } : current
    );
  }, [freshStatus, isFinished, session]);

  /** Обновление реестра — ровно один раз на успешную сессию. */
  useEffect(() => {
    if (sessionPhase !== WhatsAppAuthPhase.Success) return;
    if (successHandled.current) return;

    successHandled.current = true;
    onSuccess();
  }, [onSuccess, sessionPhase]);

  /** Токен сессии для сверки события — в рефе, чтобы не пересоздавать подписку. */
  const expectedTokenRef = useRef(session?.expectedToken ?? null);

  useEffect(() => {
    expectedTokenRef.current = session?.expectedToken ?? null;
  }, [session?.expectedToken]);

  /**
   * Событие по вебсокету только ускоряет реакцию: по нему делается внеочередной
   * запрос состояния, а решение принимается по ответу сервера.
   */
  useEffect(() => {
    if (sessionIntegrationId === null || isFinished) return;

    const unsubscribe = addWsEventListener('WhatsAppSenderQR', (payload) => {
      const event = parseWhatsAppSenderQrEvent(payload);

      if (!event.isSuccess) return;
      // Токен есть с обеих сторон — сверяем; если события приходят без токена,
      // всё равно перепроверяем статус: лишний запрос дешевле зависшего окна.
      if (
        event.token &&
        expectedTokenRef.current &&
        !isEventForToken(event.token, expectedTokenRef.current)
      ) {
        return;
      }

      refetchAuthState();
    });

    return unsubscribe;
  }, [isFinished, refetchAuthState, sessionIntegrationId]);

  /**
   * Отдельная отсечка по времени. Резолвер считает предел ожидания сам, но
   * срабатывает только на новый ответ сервера: если сервер молчит, состояние
   * не изменится и окно повиснет.
   */
  useEffect(() => {
    const limit = sessionPhase ? PHASE_TIMEOUTS[sessionPhase] : undefined;
    const sessionGeneration = session?.generation;
    if (!limit || sessionGeneration === undefined) return;

    let isCurrent = true;
    let deadlineRequest: ReturnType<typeof refetchAuthState> | null = null;

    const timer = setTimeout(() => {
      void (async () => {
        let deadlineStatus: string | null | undefined;

        try {
          deadlineRequest = refetchAuthState();
          const latestState = await withRequestTimeout(
            deadlineRequest,
            STATUS_FINAL_CHECK_TIMEOUT_MS
          );
          deadlineStatus = latestState?.Status;
        } catch {
          // Ошибка контрольного запроса не продлевает уже истёкший дедлайн.
        } finally {
          deadlineRequest = null;
        }

        if (!isCurrent) return;

        setSession((current) => {
          if (
            !current ||
            current.generation !== sessionGeneration ||
            current.phase !== sessionPhase
          ) {
            return current;
          }

          const { phase, errorMessage } = resolveWhatsAppAuthPhase({
            phase: current.phase,
            status: deadlineStatus,
            preparingElapsedMs: limit.limitMs,
            preparingTimeoutMs: PREPARING_TIMEOUT_MS,
            finishingElapsedMs: limit.limitMs,
            finishingTimeoutMs: FINISHING_TIMEOUT_MS,
          });

          phaseStartedAt.current = Date.now();
          qrFailures.current = 0;

          return {
            ...current,
            qr: null,
            phase,
            errorMessage:
              errorMessage ??
              (phase === WhatsAppAuthPhase.Error ? limit.message : undefined),
          };
        });
      })();
    }, limit.limitMs);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
      deadlineRequest?.abort?.();
    };
  }, [refetchAuthState, session?.generation, sessionPhase]);

  return {
    session,
    isOpen,
    openNew,
    openExisting,
    close,
    refreshQr,
    retry,
  };
};
